/* eslint-disable no-console */

import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
// components
import SearchCoursesScreen from "../../../components/screens/Courses/SearchCoursesScreen";
import { courses as courseQuerys } from "@nualang/nualang-api-and-queries/Queries";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import NoCoursesImage from "../../../img/noCourses.svg";
import MembersImage from "../../../img/members.svg";
// config
import config from "../../../config";
import { randomId } from "../../../utils/index";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import { getCourses } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { courses as courseHelpers, users as usersHelpers } from "../../../utils/helpers";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import { difficulties } from "../../../utils/index";
import { useApp, useCourse } from "../../../hooks";

const { languages, forLanguages } = config;

function SearchCourses(props) {
  const {
    // eslint-disable-next-line react/prop-types
    authData: { username },
    selectable,
    navigate,
    classroomId,
    authenticated,
    handleSignIn,
    learnLang = "",
    forLang = "",
    disablePadding,
    isLearnLangDisabled,
    showCreateCourseButton,
    scrollTarget,
    handleQuickCreateCourse,
    verificationStatus,
    fileSizeLimit,
  } = props;
  const queryClient = useQueryClient();

  const {
    selectedCourses,
    setSelectedCourses,
    handleClickCourse,
    setNewCourse,
    setIsJoinCourseDialogOpen,
  } = useCourse();
  const { user, openSnackbar, startLoading, stopLoading } = useApp();
  const userAttr = user.attributes !== undefined ? user.attributes : null;

  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [filters, setFilters] = useState({
    difficulty: "all",
    learnLang: learnLang
      ? learnLang
      : userAttr
        ? userAttr["custom:learnLang"]
        : "",
    forLang: forLang ? forLang : userAttr ? userAttr["custom:forLang"] : "",
    orderBy: "",
    limit: 12,
    visibility: "public",
    returnvalues: "",
    lastEvaluatedKey: "",
    lastEvaluatedCourseId: "",
    lastEvaluatedCreatedAt: "",
    lastEvaluatedExerciseStartTotal: "",
    sort: "",
    search: "",
    verbose: false,
    page: 1,
  });

  const { orderBy } = filters;

  let forLangFilter = filters.forLang;
  let learnLangFilter = filters.learnLang;

  const [sortByFilter, setSortByFilter] = useState("popular");

  useEffect(() => {
    if (
      filters.forLang !== forLangFilter &&
      filters.learnLang !== learnLangFilter
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        learnLang: learnLangFilter,
        forLang: forLangFilter,
      }));
    } else if (filters.learnLang !== learnLangFilter) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        learnLang: learnLangFilter,
      }));
    } else if (filters.forLang !== forLangFilter) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        forLang: forLangFilter,
      }));
    }
    if (filters.sort !== sortByFilter) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        sort: sortByFilter,
      }));
    }
    if (filters.search !== debouncedSearch) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        search: debouncedSearch,
      }));
    }
  }, [
    username,
    filters.forLang,
    filters.learnLang,
    filters.sort,
    filters.search,
    forLangFilter,
    learnLangFilter,
    sortByFilter,
    debouncedSearch,
  ]);

  const { ref, inView } = useInView();

  const {
    data = {},
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [...courseQuerys.courseKeys.list(filters), "search"],
    queryFn: async ({ pageParam }) => {
      try {
        const courses = await getCourses({
          ...filters,
          page: pageParam.page || 1,
          lastEvaluatedCourseId: pageParam.courseId ? pageParam.courseId : "",
          lastEvaluatedCreatedAt: pageParam.createdAt
            ? pageParam.createdAt
            : "",
          lastEvaluatedKey: pageParam.courseId ? pageParam.courseId : "",
          lastEvaluatedExerciseStartTotal:
            pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
              ? pageParam.exerciseStartTotal
              : "",
        });
        const formattedCourses = await courseHelpers.formatCourses(
          courses.Items,
          props.authData,
        );
        return {
          ...courses,
          Items: formattedCourses,
        };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.Items.length > 0 &&
        (lastPage.LastEvaluatedKey?.page || lastPage.LastEvaluatedKey?.courseId)
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
  });
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const allItems = useMemo(
    () => (data.pages || []).flatMap((page) => page.Items),
    [data],
  );

  const handleFilterChange = (event) => {
    const { target } = event;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const { name } = target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      page: 1,
      lastEvaluatedKey: "",
      lastEvaluatedCourseId: "",
      lastEvaluatedCreatedAt: "",
      lastEvaluatedExerciseStartTotal: "",
    }));
    if (name === "sort") {
      setSortByFilter(value);
    } else if (name === "search") {
      setSearch(value);
    }

    if (window.scrollY > 100) {
      window.scrollTo(0, 0);
    }
  };

  // Mutations
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/courses/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data, variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const orderKeys = ["popularity"];
  const orderOptions = orderKeys.map((key, idx) => (
    <MenuItem key={`ordercousear_${idx}${randomId()}`} value={key}>
      {t(key)}
    </MenuItem>
  ));

  const handleStartCourse = async (course) => {
    if (!authenticated) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId: course.courseId, username });
      }
    }
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const difficultyOptions = difficulties.map((d, idx) => (
    <MenuItem key={`diffcsearrole_${idx}`} value={d}>
      {t(d)}
    </MenuItem>
  ));

  const handleViewCourse = (courseId) => navigate(`/courses/${courseId}`);
  const handleCreateCourse = () => {
    navigate(
      `/classrooms/${classroomId}/create${classroomId ? `?classroomId=${classroomId}` : ""}`,
    );
  };
  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const handleViewCourseTopic = (cId, sectionId, topicId) => {
    navigate(`/courses/${cId}/${sectionId}/${topicId}`);
  };

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const handleSelectAllCoursesClick = (event) => {
    if (event.target.checked) {
      const newSelectedCourses = allItems.map((c) => c.courseId);
      setSelectedCourses(newSelectedCourses);
      return;
    }
    setSelectedCourses([]);
  };

  return (
    <React.Fragment>
      <SearchCoursesScreen
        t={t}
        isLoading={isLoading}
        placeholderImageUrl={NoCoursesImage}
        memberPlaceholderImageUrl={MembersImage}
        courses={allItems || []}
        handleStartCourse={handleStartCourse}
        handleLeaveCourse={handleLeaveCourse}
        handleViewCourse={handleViewCourse}
        learnLang={learnLangFilter}
        forLang={forLangFilter}
        sort={sortByFilter}
        orderBy={orderBy}
        handleViewCourseTopic={handleViewCourseTopic}
        languages={languages}
        forLanguages={forLanguages}
        orderOptions={orderOptions}
        handleFilterChange={handleFilterChange}
        handleCreateCourse={handleCreateCourse}
        selectable={selectable}
        selectedCourses={selectedCourses}
        handleClickCourse={handleClickCourse}
        handleSelectAllCoursesClick={handleSelectAllCoursesClick}
        getMemberDetails={getMemberDetails}
        getCourseSections={getCourseSections}
        memberId={username}
        getCourseMember={getCourseMember}
        getCourseMembers={getCourseMembers}
        username={username}
        membersProps={{
          handleViewActivity,
        }}
        disablePadding={disablePadding}
        handleViewUserProfile={handleViewUserProfile}
        isLearnLangDisabled={isLearnLangDisabled}
        showCreateCourseButton={showCreateCourseButton}
        scrollTarget={scrollTarget}
        search={search}
        setSearch={setSearch}
        difficulty={filters.difficulty}
        difficultyOptions={difficultyOptions}
        handleQuickCreateCourse={handleQuickCreateCourse}
        verificationStatus={verificationStatus}
        userImage={user ? user.picture : null}
        fileSizeLimit={fileSizeLimit}
        openSnackbar={openSnackbar}
      />
      <Box
        ref={ref}
        sx={{
          textAlign: "center",
          mt: 1
        }}>
        {isFetchingNextPage ? (
          <CircularProgress />
        ) : hasNextPage ? (
          <CircularProgress />
        ) : (
          ""
        )}
      </Box>
    </React.Fragment>
  );
}

SearchCourses.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  scrollableTarget: PropTypes.string,
  selectable: PropTypes.bool,
  learnLang: PropTypes.string,
  forLang: PropTypes.string,
  disablePadding: PropTypes.bool,
};

export default SearchCourses;
